/**
 * @file key-debug.ts
 * @description GCOME_AP 按键调试上报（key_debug）与按键校准数据解析。
 *
 * 设备开启"调试模式/设备上传按键信息"（功能变量区字节7 Bit3，经 switchDemoMode 置位）后，
 * 会持续上报按键调试包，格式见 docs/果来微科技_磁轴键盘AP通讯协议.md"按键调试上报格式"：
 * 前八字节为底层通讯格式（key_debug[0] = 0xA0 固定标志），其后为各调试字段。
 * 本文件提供：
 *  - parseGcomeKeyDebug：从上报载荷中提取 20 字节 key_debug 并解析字段；
 *  - parseCalibrationValues：解析按键校准值数组（每键 4 字节 IEEE754 单精度浮点，1.81-6.00）。
 *
 * 字段含义以协议文档为准，个别字段（key_info、hex/mm 比例）待设备联调确认。
 */

import type { GcomeCalibrationValue } from '../../../types/keyTravel'

export type { GcomeCalibrationValue }

/** key_debug 上报固定起始标志（key_debug[0]）。 */
export const GCOME_KEY_DEBUG_FLAG = 0xa0

/** key_debug 上报总长度（20 字节）。 */
export const GCOME_KEY_DEBUG_SIZE = 20

/** 校准信息区大小（512 字节 = 128 键 × 4 字节单精度浮点）。 */
export const GCOME_CALIBRATION_ZONE_SIZE = 512

/** 最大按键数（校准值/校准次数均按 128 键计）。 */
export const GCOME_CALIBRATION_KEY_COUNT = 128

/** 解析后的按键调试上报数据。 */
export interface GcomeKeyDebugUpload {
  /** 20 字节原始载荷（含 0xA0 起始标志）。 */
  raw: number[]
  /** 按键信息（3 字节，含义待联调确认）。 */
  keyInfo: [number, number, number]
  /** ADC 原始值（hex.h << 8 | hex.l）。 */
  hexValue: number
  /** 按键行程（mm 行程 0-40 档，高字节在前）。 */
  mmTravel: number
  /** hex 变化量（初始值-当前值）。 */
  hexVariation: number
  /** mm 变化量。 */
  mmVariation: number
  /** 按键校准次数（0xff = 校准完成）。 */
  calibCount: number
  /** 校准值 A.BB（个位 + 十分位 + 百分位组合，保留两位小数）。 */
  calibValue: number
  /** 行程最大值（16 位 ADC，高字节在前）。 */
  maxTravel: number
  /** 当前 ADC 值（16 位，高字节在前）。 */
  currentAdc: number
  /** 初始 ADC 值（16 位，高字节在前）。 */
  initialAdc: number
}

/**
 * 解析 key_debug 上报：载荷前 20 字节且首字节为 0xA0 时按协议字段拆解。
 * @param input 上报载荷（可为完整帧 payload 或原始 input report）
 * @returns 解析结果；不满足格式时返回 null
 */
export function parseGcomeKeyDebug(input: Uint8Array | number[]): GcomeKeyDebugUpload | null {
  const bytes = Array.from(input)
  if (bytes.length < GCOME_KEY_DEBUG_SIZE || bytes[0] !== GCOME_KEY_DEBUG_FLAG) return null

  return {
    raw: bytes.slice(0, GCOME_KEY_DEBUG_SIZE),
    keyInfo: [bytes[1], bytes[2], bytes[3]],
    hexValue: (bytes[4] << 8) | bytes[5],
    mmTravel: (bytes[6] << 8) | bytes[7],
    hexVariation: bytes[8],
    mmVariation: bytes[9],
    calibCount: bytes[10],
    calibValue: bytes[11] + bytes[12] / 10 + bytes[13] / 100,
    maxTravel: (bytes[14] << 8) | bytes[15],
    currentAdc: (bytes[16] << 8) | bytes[17],
    initialAdc: (bytes[18] << 8) | bytes[19],
  }
}

/**
 * 解析按键校准值数组（每键 4 字节 IEEE754 单精度浮点，小端序，范围 1.81-6.00）。
 * @param data 校准信息区数据（512 字节，可分帧拼接）
 * @param keyCount 最大按键数，默认 128
 */
export function parseCalibrationValues(
  data: Uint8Array | number[],
  keyCount = GCOME_CALIBRATION_KEY_COUNT,
): GcomeCalibrationValue[] {
  const bytes = Array.from(data)
  const items: GcomeCalibrationValue[] = []
  for (let index = 0; index < keyCount; index++) {
    const base = index * 4
    if (base + 4 > bytes.length) break
    const view = new DataView(new Uint8Array(bytes.slice(base, base + 4)).buffer)
    items.push({ index, value: view.getFloat32(0, true) })
  }
  return items
}
